import { Button } from "@legal-500/legal500-design-system";
import { useEffect, useState } from "react";
import { masterCompaniesService, type WebsiteCompany } from "../../../api/masterCompanies.service";
import type { CompanyReviewEvent } from "../../../api/pendingCompanies.service";
import { formatDateTime } from "../../../utils";
import { CompanyHistory } from "../CompanyHistory";
import { RecordDetailsModal } from "../components/RecordDetailsModal";
import styles from "../PendingCompanies/Review.module.css";

export function CompanyDetailsPanel({
  company,
  onClose,
}: {
  company: WebsiteCompany;
  onClose: () => void;
}) {
  const [events, setEvents] = useState<CompanyReviewEvent[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    masterCompaniesService
      .audit(company.id, 10, (page - 1) * 10)
      .then(result => {
        if (!active) return;
        setEvents(result.data);
        setTotal(result.meta.total);
      })
      .catch(caught => {
        if (active)
          setError(caught instanceof Error ? caught.message : "Unable to load company history.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [company.id, page, retry]);
  return (
    <RecordDetailsModal
      title="Company Details"
      onClose={onClose}
      details={
        <dl className={styles.facts}>
          <dt>Name</dt>
          <dd>{company.name}</dd>
          <dt>Website</dt>
          <dd>{company.website || "—"}</dd>
          <dt>Employees</dt>
          <dd>{company.employees || "—"}</dd>
          <dt>Industry</dt>
          <dd>{company.industrySector || "—"}</dd>
          <dt>Created</dt>
          <dd>{formatDateTime(company.createdAt)}</dd>
          <dt>ID</dt>
          <dd>{company.id}</dd>
        </dl>
      }
      history={
        <>
          {loading && <p role="status">Loading company history…</p>}
          {error && (
            <div role="alert">
              {error}{" "}
              <Button
                color="primary"
                className={styles.brandButton}
                variant="outlined"
                onClick={() => setRetry(value => value + 1)}
              >
                Retry history
              </Button>
            </div>
          )}
          {!loading && !error && (
            <CompanyHistory
              events={events}
              page={page}
              total={total}
              onPageChange={setPage}
              emptyMessage="No recorded history. Companies created before audit tracking may have no creation event."
            />
          )}
        </>
      }
    />
  );
}
